import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Search, Check, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "../api";
import { Button, Input, Field } from "./common";

/**
 * "Paste your website and we'll fill this in" -- the entry point to brand
 * discovery.
 *
 * The panel proposes; it never applies on its own. Every row is a checkbox the
 * admin ticks, and applying goes through the same two endpoints the manual
 * form uses (`PUT /settings/branding` for values, `.../assets/{kind}/from-url`
 * for images). That is deliberate and load-bearing rather than cautious:
 * `GET /api/branding` is unauthenticated because the login screen renders from
 * it, so a wrong value is visible to everyone before anyone signs in, and a
 * scraped value is a guess. An "apply everything automatically" button must
 * never be added here.
 *
 * Only `high`-confidence rows start ticked. A suggestion an admin cannot weigh
 * is worse than no suggestion, which is why every row shows the source it came
 * from -- "manifest name" and "page title" deserve very different trust.
 *
 * The selection is lifted to the parent through `onOverlayChange` so the live
 * <BrandPreview> can render the *proposed* instance. That connection is what
 * makes the feature trustworthy: without it an admin is approving `#0F766E` on
 * faith, and with it they see the colour on their own UI and can reject it in
 * a second.
 */

// Mirrors branding_assets.KIND_TO_FIELD, inverted. These fields hold images,
// so applying one downloads bytes through the from-url route rather than
// writing a string -- the login page must not depend on a third party's CDN.
const FIELD_TO_KIND = {
  logo_url: "logo",
  logo_icon_url: "logo_icon",
  favicon_url: "favicon",
  apple_touch_icon_url: "apple_touch_icon",
  login_image_url: "login_image",
};

const COLOR_FIELDS = new Set(["primary", "primary_hover", "glow", "theme_color"]);

// Display order and labels, following the manual form's own grouping so the
// two read as one card rather than two features stacked.
const FIELD_ORDER = [
  ["org_name", "branding.orgName"],
  ["wordmark_text", "branding.wordmarkText"],
  ["wordmark_accent", "branding.wordmarkAccent"],
  ["tagline", "branding.tagline"],
  ["document_title", "branding.documentTitle"],
  ["org_description", "branding.orgDescription"],
  ["meta_description", "branding.metaDescription"],
  ["primary", "branding.primary"],
  ["primary_hover", "branding.primaryHover"],
  ["glow", "branding.glow"],
  ["theme_color", "branding.themeColor"],
  ["logo_icon_url", "branding.logoIconUrl"],
  ["logo_url", "branding.logoUrl"],
  ["favicon_url", "branding.faviconUrl"],
  ["apple_touch_icon_url", "branding.appleTouchIconUrl"],
  ["login_image_url", "branding.loginImageUrl"],
  ["internal_domains", "branding.internalDomains"],
  ["google_hd", "branding.googleHd"],
];

const CONFIDENCE_CLASS = {
  high: "text-success",
  medium: "text-muted",
  low: "text-muted opacity-70",
};

function ValuePreview({ field, value }) {
  if (value === "" || value == null) {
    return <span className="text-muted italic">—</span>;
  }
  if (Array.isArray(value)) {
    return <span className="truncate">{value.join(", ")}</span>;
  }
  if (COLOR_FIELDS.has(field)) {
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0">
        <span className="w-3.5 h-3.5 rounded-sm border border-border shrink-0"
          style={{ background: value }} />
        <span className="font-mono truncate">{value}</span>
      </span>
    );
  }
  if (field.endsWith("_url")) {
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0">
        {/* The proposed URL is still remote at this point. Loading it
            directly is fine -- a browser fetching the customer's own site is
            not the SSRF concern the backend guards against. A failed load
            leaves the bordered tile, which reads as "no preview" rather than
            as a broken image. */}
        <span className="w-5 h-5 shrink-0 rounded-sm border border-border bg-surface
                         flex items-center justify-center overflow-hidden">
          <img src={value} alt="" className="max-w-full max-h-full object-contain"
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
        </span>
        <span className="truncate" title={value}>
          {decodeURIComponent(value.split("/").pop().split("?")[0]) || value}
        </span>
      </span>
    );
  }
  return <span className="truncate" title={String(value)}>{String(value)}</span>;
}

export default function BrandDiscoveryPanel({ current, onOverlayChange, onApplied, onError }) {
  const { t } = useTranslation();
  const [website, setWebsite] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [applying, setApplying] = useState(false);

  // Rows in display order, skipping anything the site didn't yield and
  // anything whose proposal equals what is already set -- a row that changes
  // nothing is noise in a list the admin has to read.
  const rows = useMemo(() => {
    if (!result) return [];
    return FIELD_ORDER
      .filter(([field]) => field in result.fields)
      .filter(([field]) => {
        const proposed = result.fields[field].value;
        const existing = current?.[field];
        return JSON.stringify(proposed) !== JSON.stringify(existing);
      })
      .map(([field, labelKey]) => ({ field, labelKey, ...result.fields[field] }));
  }, [result, current]);

  const overlay = useMemo(() => {
    const out = {};
    for (const row of rows) {
      if (selected.has(row.field)) out[row.field] = row.value;
    }
    return out;
  }, [rows, selected]);

  useEffect(() => { onOverlayChange?.(overlay); }, [overlay, onOverlayChange]);

  const reset = useCallback(() => {
    setResult(null);
    setSelected(new Set());
    onOverlayChange?.({});
  }, [onOverlayChange]);

  const fetchProposal = async () => {
    if (!website.trim()) return;
    setLoading(true);
    onError?.("");
    reset();
    try {
      const r = await api.post("/settings/branding/discover", { website: website.trim() });
      setResult(r.data);
      // Pre-tick only what the site stated outright. Everything derived or
      // guessed stays unticked, so the default action is conservative.
      setSelected(new Set(
        Object.entries(r.data.fields)
          .filter(([, v]) => v.confidence === "high")
          .map(([field]) => field)));
    } catch (e) {
      console.error("Brand discovery failed:", e);
      onError?.(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const toggle = (field) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(field) ? next.delete(field) : next.add(field);
    return next;
  });

  const setAll = (on) =>
    setSelected(on ? new Set(rows.map((r) => r.field)) : new Set());

  const apply = async () => {
    setApplying(true);
    onError?.("");
    const chosen = rows.filter((r) => selected.has(r.field));
    const values = {};
    const assets = [];
    for (const row of chosen) {
      if (FIELD_TO_KIND[row.field]) assets.push(row);
      else values[row.field] = row.value;
    }

    let record = null;
    const failures = [];
    try {
      // Values first, images second. Each image POST returns the whole record,
      // so the last successful response is the freshest truth.
      if (Object.keys(values).length) {
        record = (await api.put("/settings/branding", values)).data;
      }
      for (const row of assets) {
        try {
          record = (await api.post(
            `/settings/branding/assets/${FIELD_TO_KIND[row.field]}/from-url`,
            { url: row.value })).data;
        } catch (e) {
          // Per-slot, not fatal: one refused image (an SVG, or a URL that has
          // gone stale) must not look like the whole apply failed, and the
          // backend's own message explains what to do about it.
          failures.push(`${t(row.labelKey)}: ${formatApiError(e.response?.data?.detail) || e.message}`);
        }
      }
      if (record) onApplied?.(record);
      if (failures.length) onError?.(failures.join(" · "));
      if (!failures.length) reset();
    } catch (e) {
      console.error("Applying discovered branding failed:", e);
      onError?.(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="rounded-sm border border-border bg-surface p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Globe size={16} className="text-primary mt-0.5 shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium">{t("branding.discovery.title")}</p>
          <p className="text-xs text-muted">{t("branding.discovery.desc")}</p>
        </div>
      </div>

      <div className="flex items-end gap-2">
        <div className="flex-1 min-w-0">
          <Field label={t("branding.discovery.websiteLabel")}>
            <Input data-testid="discovery-website" value={website}
              onChange={(e) => setWebsite(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); fetchProposal(); } }}
              placeholder="acme.io" />
          </Field>
        </div>
        <Button onClick={fetchProposal} disabled={loading || !website.trim()}
          data-testid="discovery-fetch">
          {loading
            ? <><Loader2 size={16} className="animate-spin" /> {t("branding.discovery.fetching")}</>
            : <><Search size={16} /> {t("branding.discovery.fetch")}</>}
        </Button>
      </div>

      {result && (
        <div className="space-y-2 pt-1" data-testid="discovery-result">
          {result.partial && (
            <p className="text-xs text-danger">{t("branding.discovery.partial")}</p>
          )}
          {result.notes.map((note, i) => (
            <p key={i} className="text-xs text-muted">{note}</p>
          ))}

          {rows.length === 0 ? (
            <p className="text-sm text-muted">{t("branding.discovery.nothingNew")}</p>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-muted">
                  {t("branding.discovery.found", { count: rows.length })}
                </span>
                <span className="flex items-center gap-2">
                  <button type="button" onClick={() => setAll(true)}
                    className="text-primary hover:underline">{t("branding.discovery.selectAll")}</button>
                  <button type="button" onClick={() => setAll(false)}
                    className="text-muted hover:underline">{t("branding.discovery.selectNone")}</button>
                </span>
              </div>

              <div className="divide-y divide-border border border-border rounded-sm">
                {rows.map((row) => (
                  <label key={row.field} data-testid={`discovery-row-${row.field}`}
                    className="flex items-start gap-2.5 p-2.5 cursor-pointer hover:bg-bg transition-colors">
                    <input type="checkbox" checked={selected.has(row.field)}
                      onChange={() => toggle(row.field)}
                      className="mt-0.5 shrink-0 accent-primary" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-xs font-medium">{t(row.labelKey)}</span>
                        <span className={`text-[10px] ${CONFIDENCE_CLASS[row.confidence] || "text-muted"}`}>
                          {row.source}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-xs mt-1 min-w-0">
                        <span className="text-muted line-through decoration-1 min-w-0 max-w-[45%] flex">
                          <ValuePreview field={row.field} value={current?.[row.field]} />
                        </span>
                        <span className="text-muted shrink-0">→</span>
                        <span className="min-w-0 flex-1 flex">
                          <ValuePreview field={row.field} value={row.value} />
                        </span>
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <Button onClick={apply} disabled={applying || selected.size === 0}
                  data-testid="discovery-apply">
                  {applying
                    ? <><Loader2 size={16} className="animate-spin" /> {t("branding.discovery.applying")}</>
                    : <><Check size={16} /> {t("branding.discovery.apply", { count: selected.size })}</>}
                </Button>
                <button type="button" onClick={reset} data-testid="discovery-discard"
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm text-muted
                             hover:bg-bg hover:text-txt transition-colors border border-border">
                  <X size={14} /> {t("branding.discovery.discard")}
                </button>
              </div>
              <p className="text-xs text-muted">{t("branding.discovery.applyHint")}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
